/**
 * One set of date conditions for cards, the table filter, Links and OS (F21 of
 * docs/plans/field-relations.md; owner, 27 Oct: "it should be done via a
 * query, not model math").
 *
 *   is_today, before_today, after_today
 *   in_next_days N, in_last_days N   N days, today counted as the first
 *   on_or_before <date>, on_or_after <date>
 *
 * Each is a range of calendar days worked out with the caller's day: the
 * workspace's on the server, the device's on the phone and the web (F8). One
 * evaluator (`dateConditionHolds`) and one SQL builder (`dateConditionSql`)
 * read the same range (`dateConditionRange`), so a card, the list and the
 * server answer the same rows. A value is read as its day, the first ten
 * characters, the way `plainDayOf` reads a stored date.
 */
import { addToDay, plainDayOf, workedResultOf } from './formula';

export type DateConditionOp =
  | 'is_today'
  | 'before_today'
  | 'after_today'
  | 'in_next_days'
  | 'in_last_days'
  | 'on_or_before'
  | 'on_or_after';

/** Every date condition, in the order a picker offers them. */
export const DATE_CONDITION_OPS = [
  'is_today',
  'before_today',
  'after_today',
  'in_next_days',
  'in_last_days',
  'on_or_before',
  'on_or_after',
] as const satisfies readonly DateConditionOp[];

const OPS: ReadonlySet<string> = new Set(DATE_CONDITION_OPS);

export const isDateConditionOp = (value: unknown): value is DateConditionOp => typeof value === 'string' && OPS.has(value);

/**
 * Whether a field holds a day a date condition can ask: a stored date, and a
 * worked-out date or a date total, since each keeps its day in its slot.
 */
export const asksDateCondition = (field: { readonly type: string; readonly spec?: unknown }): boolean =>
  field.type === 'date' || ((field.type === 'formula' || field.type === 'rollup') && workedResultOf(field.type, field.spec) === 'date');

/** The two that take a count of days. */
export const dateOpTakesDays = (op: DateConditionOp): boolean => op === 'in_next_days' || op === 'in_last_days';

/** The two that take a date. */
export const dateOpTakesDate = (op: DateConditionOp): boolean => op === 'on_or_before' || op === 'on_or_after';

/** The most days `in_next_days` and `in_last_days` reach. */
export const DATE_CONDITION_MAX_DAYS = 3660;

/**
 * What is wrong with the value one date condition carries, or null. The three
 * "today" conditions carry none; a count of days is a whole number from 1; a
 * date is `YYYY-MM-DD`.
 */
export const dateConditionValueProblem = (op: DateConditionOp, value: unknown): string | null => {
  if (dateOpTakesDays(op)) {
    const days = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
    return typeof days === 'number' && Number.isInteger(days) && days >= 1 && days <= DATE_CONDITION_MAX_DAYS
      ? null
      : `"${op}" takes a whole number of days, from 1 to ${DATE_CONDITION_MAX_DAYS}.`;
  }
  if (dateOpTakesDate(op)) {
    return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && plainDayOf(value) === value
      ? null
      : `"${op}" takes a date, written YYYY-MM-DD.`;
  }
  return null;
};

/** A range of calendar days, both ends counted; null leaves that side open. */
export type DayRange = { readonly from: string | null; readonly to: string | null };

/**
 * The days one condition holds for, on `today` (`YYYY-MM-DD`). Null when the
 * value does not read or today is not a day: such a condition holds for no row.
 */
export const dateConditionRange = (op: DateConditionOp, value: unknown, today: string): DayRange | null => {
  if (plainDayOf(today) !== today) return null;
  if (dateConditionValueProblem(op, value) !== null) return null;
  const days = Number(value);
  const shift = (amount: number): string | null => addToDay(today, amount, 'days');
  switch (op) {
    case 'is_today':
      return { from: today, to: today };
    case 'before_today':
      return { from: null, to: shift(-1) };
    case 'after_today':
      return { from: shift(1), to: null };
    case 'in_next_days':
      return { from: today, to: shift(days - 1) };
    case 'in_last_days':
      return { from: shift(-(days - 1)), to: today };
    case 'on_or_before':
      return { from: null, to: String(value) };
    case 'on_or_after':
      return { from: String(value), to: null };
  }
};

/** Whether a stored value's day falls in one condition's range. An empty value never does. */
export const dateConditionHolds = (op: DateConditionOp, value: unknown, held: unknown, today: string | null): boolean => {
  if (today === null) return false;
  const day = plainDayOf(held);
  if (day === null) return false;
  const range = dateConditionRange(op, value, today);
  if (range === null) return false;
  return (range.from === null || day >= range.from) && (range.to === null || day <= range.to);
};

/**
 * One date condition as SQL, on an expression that answers the stored day as
 * `YYYY-MM-DD` text (`substr(…, 1, 10)` on the phone's SQLite; `to_char(slot
 * at time zone 'UTC', 'YYYY-MM-DD')` on Postgres). `bind` adds a value and
 * answers its placeholder. A condition that cannot hold answers `1 = 0`.
 */
export const dateConditionSql = (
  dayExpr: string,
  op: DateConditionOp,
  value: unknown,
  today: string,
  bind: (day: string) => string,
): string => {
  const range = dateConditionRange(op, value, today);
  if (range === null) return '1 = 0';
  const parts = [`${dayExpr} is not null`];
  if (range.from !== null) parts.push(`${dayExpr} >= ${bind(range.from)}`);
  if (range.to !== null) parts.push(`${dayExpr} <= ${bind(range.to)}`);
  return `(${parts.join(' and ')})`;
};
